import {
  extendVariants,
  Select as NextUISelect,
  SelectItem as NextUISelectItem,
} from "@heroui/react";
import clsx from "clsx";
import { WithId } from "core/types";
import { ReactNode, useMemo } from "react";

const ExtendedSelect = extendVariants(NextUISelect, {
  compoundVariants: [
    // [inline, outline] & color
    {
      class: {
        // @ts-ignore
        trigger: [
          "data-[open=true]:ring-primary",
          "data-[focus=true]:ring-primary",
        ],
      },
      color: "primary",
      variant: ["inline", "outline"],
    },
    // labelPlacement=outside & default
    {
      class: {
        // @ts-ignore
        label: "group-data-[filled=true]:text-gray-900",
      },
      color: "default",
      labelPlacement: "outside",
    },
    // isDisabled & variant
    {
      class: {
        // @ts-ignore
        label: "!text-default-disabled",
        trigger: ["bg-white !text-default-disabled"],
        value: "!text-default-disabled",
      },
      isDisabled: true,
      variant: ["minimal", "horizontal"],
    },
    // isInvalid & variant
    {
      class: {
        // @ts-ignore
        trigger: "!ring-danger group-data-[focus=true]:ring-danger",
      },
      isInvalid: true,
      variant: ["inline", "outline"],
    },
    {
      class: {
        // @ts-ignore
        trigger: "bg-white",
      },
      isInvalid: true,
      variant: ["minimal", "horizontal"],
    },
    // isInvalid & variant & size
    {
      class: {
        // @ts-ignore
        errorMessage: "pl-2",
      },
      isInvalid: true,
      size: "md",
      variant: ["minimal", "horizontal"],
    },
    {
      class: {
        // @ts-ignore
        errorMessage: "pl-2.5",
      },
      isInvalid: true,
      size: "lg",
      variant: ["minimal", "horizontal"],
    },
    // size & labelPlacement
    {
      class: {
        // @ts-ignore
        trigger: "h-8 min-h-8 py-1.5 px-3",
      },
      labelPlacement: "inside",
      size: "sm",
    },
    {
      class: {
        // @ts-ignore
        trigger: "h-10 min-h-10 py-2",
      },
      labelPlacement: "inside",
      size: "md",
    },
    {
      class: {
        // @ts-ignore
        label: "text-sm",
        trigger: "h-12 min-h-12 py-2.5 gap-0",
      },
      labelPlacement: "inside",
      size: "lg",
    },
    {
      class: {
        // @ts-ignore
        base: "group relative justify-end",
        label: [
          "pb-0",
          "z-20",
          "top-1/2",
          "-translate-y-1/2",
          "group-data-[filled=true]:start-0",
        ],
      },
      isMultiline: false,
      labelPlacement: "outside",
    },
    {
      class: {
        // @ts-ignore
        base: "group relative justify-end",
        label: [
          "pb-0",
          "z-20",
          "top-1/2",
          "-translate-y-1/2",
          "group-data-[filled=true]:start-0",
        ],
      },
      isMultiline: false,
      labelPlacement: "outside",
    },
    // labelPlacement=[inside]
    {
      class: {
        // @ts-ignore
        label:
          "group-data-[filled=true]:scale-100 group-data-[filled=true]:text-[10px]",
      },
      labelPlacement: ["inside"],
    },
    // inside & size
    {
      class: {
        // @ts-ignore
        label: "text-sm",
      },
      labelPlacement: "inside",
      size: ["sm", "md"],
    },
    {
      class: {
        // @ts-ignore
        innerWrapper: "group-data-[has-label=true]:pt-4",
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_theme(fontSize.sm)/2_-_8px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "sm",
    },
    {
      class: {
        // @ts-ignore
        innerWrapper: "group-data-[has-label=true]:pt-4",
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_6px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "md",
    },
    {
      class: {
        // @ts-ignore
        innerWrapper: "group-data-[has-label=true]:pt-5",
        label: [
          "text-sm",
          "group-data-[filled=true]:-translate-y-[calc(50%_+__10px/2_-_8px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "lg",
    },
    // inside & size & inline
    {
      class: {
        // @ts-ignore
        innerWrapper: "group-data-[has-label=true]:pt-3",
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_8px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "sm",
      variant: "inline",
    },
    {
      class: {
        // @ts-ignore
        innerWrapper: "group-data-[has-label=true]:pt-3",
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_6px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "md",
      variant: "inline",
    },
    {
      class: {
        // @ts-ignore
        innerWrapper: "group-data-[has-label=true]:pt-3",
        label: [
          "text-sm",
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_6px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "lg",
      variant: "inline",
    },
    // inside & size & minimal
    {
      class: {
        // @ts-ignore
        innerWrapper: "group-data-[has-label=true]:pt-2",
        label: ["!text-sm !translate-y-0"],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "sm",
      variant: "minimal",
    },
    {
      class: {
        // @ts-ignore
        innerWrapper: "group-data-[has-label=true]:pt-2",
        label: ["!text-sm !translate-y-0"],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "md",
      variant: "minimal",
    },
    {
      class: {
        // @ts-ignore
        innerWrapper: "group-data-[has-label=true]:pt-2",
        label: ["!text-sm !translate-y-0"],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "lg",
      variant: "minimal",
    },
    // outside & size
    {
      class: {
        // @ts-ignore
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_8px)]",
        label: [
          "start-0",
          "text-sm",
          "font-medium text-gray-900",
          "!-translate-y-[calc(100%_+_theme(fontSize.sm)/2_+_16px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "outside",
      size: "sm",
    },
    {
      class: {
        // @ts-ignore
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_10px)]",
        label: [
          "start-0",
          "text-sm",
          "font-medium text-gray-900",
          "!-translate-y-[calc(100%_+_theme(fontSize.sm)/2_+_20px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "outside",
      size: "md",
    },
    {
      class: {
        // @ts-ignore
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_12px)]",
        label: [
          "start-0",
          "text-sm",
          "font-medium text-gray-900",
          "!-translate-y-[calc(100%_+_theme(fontSize.sm)/2_+_24px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "outside",
      size: "lg",
    },
    // outside-left & size
    {
      class: {
        // @ts-ignore
        label: ["h-8 flex items-center"],
      },
      isMultiline: false,
      labelPlacement: "outside-left",
      size: "sm",
    },
    {
      class: {
        // @ts-ignore
        label: ["h-10 flex items-center"],
      },
      isMultiline: false,
      labelPlacement: "outside-left",
      size: "md",
    },
    {
      class: {
        // @ts-ignore
        label: ["h-12 flex items-center"],
      },
      isMultiline: false,
      labelPlacement: "outside-left",
      size: "lg",
    },
    // isMultiline & labelPlacement="outside"
    {
      class: {
        // @ts-ignore
        label: "pb-1.5",
      },
      isMultiline: true,
      labelPlacement: "outside",
    },
    // text truncate labelPlacement=[inside,outside]
    {
      class: {
        // @ts-ignore
        label: ["pe-2", "max-w-full", "text-ellipsis", "overflow-hidden"],
      },
      labelPlacement: ["inside", "outside"],
    },
  ],
  defaultVariants: {
    color: "primary",
    disableSelectorIconRotation: false,
    fullWidth: true,
    isDisabled: false,
    isMultiline: false,
    labelPlacement: "inside",
    size: "md",
    variant: "inline",
  },
  variants: {
    color: {
      danger: {},
      default: {},
      primary: {},
      secondary: {},
      success: {},
      warning: {},
    },
    disableAnimation: {
      false: {
        base: "transition-background motion-reduce:transition-none !duration-150",
        label: [
          "will-change-auto",
          "origin-top-left",
          // Using RTL here as Tailwind CSS doesn't support `start` and `end` logical properties for transforms yet.
          "rtl:origin-top-right",
          "!duration-200",
          "!ease-out",
          "transition-[transform,color,left,opacity]",
          "motion-reduce:transition-none",
        ],
        selectorIcon:
          "transition-transform duration-150 ease motion-reduce:transition-none",
      },
      true: {
        base: "transition-none",
        label: "transition-none",
        selectorIcon: "transition-none",
        trigger: "after:transition-none",
      },
    },
    disableSelectorIconRotation: {
      false: {
        selectorIcon: "data-[open=true]:rotate-180",
      },
      true: {},
    },
    fullWidth: {
      false: {
        base: "min-w-40",
      },
      true: {
        base: "w-full",
      },
    },
    isDisabled: {
      true: {
        base: "opacity-100 pointer-events-none",
        trigger: "pointer-events-none bg-gray-200",
      },
    },
    isInvalid: {
      true: {
        errorMessage: "text-danger-dark",
        label: "text-danger",
        selectorIcon: "text-danger",
        trigger: "border-danger",
        value: "text-danger",
      },
    },
    isMultiline: {
      false: {
        value: "truncate",
      },
      true: {
        label: "relative min-h-fit",
        trigger: "!h-auto",
      },
    },
    isRequired: {
      true: {
        label: "after:content-['*'] after:text-danger after:ms-0.5",
      },
    },
    labelPlacement: {
      inside: {
        label: "text-sm cursor-pointer",
        trigger: "flex-col items-start justify-center gap-0",
      },
      outside: {
        base: "flex flex-col",
        label: "text-sm",
        value: "font-normal text-sm",
      },
      "outside-left": {
        base: "flex-row items-center flex-nowrap items-start",
        label: "relative pe-2 text-foreground flex items-center",
      },
    },
    radius: {
      full: {
        trigger: "rounded-full",
      },
      lg: {
        trigger: "rounded-large",
      },
      md: {
        trigger: "rounded-medium",
      },
      none: {
        trigger: "rounded-none",
      },
      sm: {
        trigger: "rounded-small",
      },
    },
    size: {
      lg: {
        trigger: "h-12 min-h-12 rounded-large",
        value: "text-medium",
      },
      md: {
        trigger: "h-10 min-h-10 rounded-medium",
        value: "text-small",
      },
      sm: {
        label: "text-tiny",
        trigger: "h-8 min-h-8 px-2 rounded-small",
        value: "text-small",
      },
    },
    variant: {
      horizontal: {
        innerWrapper: ["!pt-0"],
        label:
          "min-w-max font-medium group-data-[hover=true]:text-gray-900 !text-sm",
        listbox: "[&>*]:gap-1 min-w-full w-max overflow-y-auto max-h-60",
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-transparent",
          "data-[hover=true]:bg-white",
          "data-[open=true]:bg-white",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
          "w-auto min-w-[80px]",
        ],
        value: [
          "group-data-[has-value=true]:text-gray-900 !text-sm font-normal group-data-[hover=true]:text-gray-900",
        ],
      },
      inline: {
        label:
          "text-gray-600 group-data-[hover=true]:text-gray-900 text-sm font-normal group-data-[has-value=true]:font-medium",
        listbox: "[&>*]:gap-1 overflow-y-auto max-h-60", // necessary due to NextUI bug - both the div and the ul has the data-slot="listbox"
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-gray-600",
          "data-[hover=true]:ring-black data-[hover=true]:ring-2 data-[hover=true]:bg-white",
          "data-[open=true]:ring-primary data-[open=true]:ring-2",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
        ],
        value: "group-data-[has-value=true]:text-gray-900 !text-sm font-normal",
      },
      minimal: {
        innerWrapper: ["!pt-0"],
        label:
          "group-data-[has-value=true]:!hidden group-data-[hover=true]:text-gray-900",
        listbox: "[&>*]:gap-1 overflow-y-auto max-h-60", // necessary due to NextUI bug - both the div and the ul has the data-slot="listbox"
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-transparent",
          "data-[hover=true]:bg-white",
          "data-[open=true]:bg-white",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
        ],
        value: [
          "group-data-[has-value=true]:text-gray-900 !text-sm font-normal",
        ],
      },
      outline: {
        label: [
          "text-gray-600 group-data-[hover=true]:text-gray-900 text-sm font-medium",
        ],
        listbox: "[&>*]:gap-1 overflow-y-auto max-h-60", // necessary due to NextUI bug - both the div and the ul has the data-slot="listbox"
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-gray-600",
          "data-[hover=true]:ring-black data-[hover=true]:ring-2 data-[hover=true]:bg-white",
          "data-[open=true]:ring-primary data-[open=true]:ring-2",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
        ],
        value: "group-data-[has-value=true]:text-gray-900 !text-sm font-normal",
      },
    },
  },
});

type ExtendedSelectProps = React.ComponentProps<typeof ExtendedSelect>;

type Variant = Extract<
  ExtendedSelectProps["variant"],
  "inline" | "outline" | "minimal" | "horizontal"
>;

export type SelectProps<T extends WithId = WithId> = Omit<
  ExtendedSelectProps,
  "variant" | "color" | "radius" | "labelPlacement" | "children"
> & {
  items: T[];
  renderItem: (item: T) => ReactNode;
  variant?: Variant;
};

function useSelectedKeysConvert<T>(selectedKeys: T): Set<string> {
  return useMemo(() => {
    if (selectedKeys == null) return new Set<string>();
    if (selectedKeys instanceof Set) return selectedKeys as Set<string>;
    if (Array.isArray(selectedKeys)) return new Set(selectedKeys.map(String));
    return new Set([String(selectedKeys)]);
  }, [selectedKeys]);
}

function getLabelPlacement(variant?: Variant) {
  if (variant === "inline" || variant === "minimal") return "inside";
  if (variant === "outline") return "outside";
  if (variant === "horizontal") return "outside-left";
  return undefined;
}

export function Select<T extends WithId = WithId>({
  renderItem,
  selectedKeys,
  ...props
}: SelectProps<T>) {
  const convertToSet = useSelectedKeysConvert(selectedKeys);
  const labelPlacement = useMemo(
    () => getLabelPlacement(props.variant),
    [props.variant],
  );

  return (
    <ExtendedSelect
      {...props}
      color="primary"
      data-testid={props.name}
      labelPlacement={labelPlacement}
      selectedKeys={convertToSet}
      title={typeof props.title === "string" ? props.title : undefined}
    >
      {(item: object) => {
        const typedItem = item as T;
        return (
          <NextUISelectItem
            key={typedItem.id}
            classNames={{
              base: clsx(
                "!text-gray-900",
                "rounded-lg !bg-white p-2 !outline-none ring-1 ring-transparent",
                "data-[focus=true]:!bg-primary-light data-[focus=true]:ring-primary",
                "data-[hover=true]:!bg-primary-light data-[hover=true]:ring-primary",
                "data-[selected=true]:!bg-primary-light data-[selected=true]:font-semibold data-[selected=true]:ring-primary",
              ),
            }}
          >
            {renderItem(typedItem)}
          </NextUISelectItem>
        );
      }}
    </ExtendedSelect>
  );
}

Select.displayName = "RecareUI.Select";
